'use client';
import {createStore, type StoreApi} from '@lib/create-store';
import type {McpManifestApiServiceModel, SystemName} from '@linx-security/api-specs';
import {createContext, useContext} from 'react';
import {shallow} from 'zustand/shallow';
import {useStoreWithEqualityFn} from 'zustand/traditional';

type McpManifestsStore = {
  manifests: Map<SystemName, McpManifestApiServiceModel>;
  isManifestsLoading: boolean;

  setManifests: (manifests: Array<McpManifestApiServiceModel>) => void;
  setIsManifestsLoading: (isLoading: boolean) => void;
};

export type McpManifestsStoreApi = StoreApi<McpManifestsStore>;

export const createMcpManifestsStore = () => {
  return createStore<McpManifestsStore>(
    set => ({
      manifests: new Map(),
      isManifestsLoading: true,
      setManifests: (manifests: Array<McpManifestApiServiceModel>) => {
        const map = new Map();
        manifests.forEach(manifest => {
          map.set(manifest.system_name, manifest);
        });
        set({manifests: map});
      },
      setIsManifestsLoading: (isLoading: boolean) => {
        set({isManifestsLoading: isLoading});
      },
    }),
    {name: 'McpManifestsStore', version: 1},
  );
};

export const useMcpManifestsStore = <U>(
  selector: (state: McpManifestsStore) => U,
  equalityFn: (left: U, right: U) => boolean = shallow,
): U => {
  const context = useContext(McpManifestsContext);
  if (!context) {
    throw new Error('useMcpManifestsStore must be used within a McpManifestsProvider');
  }
  return useStoreWithEqualityFn(context, selector, equalityFn);
};

export const McpManifestsContext = createContext<ReturnType<typeof createMcpManifestsStore> | null>(null);
